import type { ValidationIssue, ValidationIssueCategory, ValidationIssueSeverity } from "./validation-issue.js";
import type { InspectionBook, InspectionBookItem } from "./inspection-book.js";
import { domainId, type ValidationIssueId } from "../../shared/kernel/identifiers.js";

export interface ValidationContextData {
  readonly book: InspectionBook;
  readonly inspection?: any;
  readonly equipment?: any;
  readonly checklist?: any;
  readonly report?: any;
  readonly recommendations?: readonly any[];
  readonly ndtReports?: readonly any[];
  readonly avr?: any;
}

export class TechnicalValidationEngine {
  public validate(context: ValidationContextData): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const { book, inspection, equipment, checklist, report, recommendations = [], ndtReports = [], avr } = context;
    const clientId = book.clientId;
    const inspectionId = book.inspectionId;
    const now = new Date().toISOString();

    const createIssue = (
      category: ValidationIssueCategory,
      severity: ValidationIssueSeverity,
      title: string,
      description: string,
      ruleCode: string,
      options?: {
        overrideAllowed?: boolean;
        evidence?: string;
        suggestedAction?: string;
        section?: string;
        field?: string;
        documentType?: string;
      }
    ): ValidationIssue => {
      const id = domainId<ValidationIssueId>(crypto.randomUUID());
      const issue: any = {
        id,
        clientId,
        inspectionId,
        bookId: book.id,
        category,
        severity,
        source: 'RULE_ENGINE',
        title,
        description,
        detectedBy: 'TechnicalValidationEngine v1.0',
        ruleCode,
        status: 'OPEN',
        overrideAllowed: options?.overrideAllowed ?? true,
        createdAt: now,
        updatedAt: now,
      };
      if (options?.evidence) issue.evidence = options.evidence;
      if (options?.suggestedAction) issue.suggestedAction = options.suggestedAction;
      if (options?.section) issue.section = options.section;
      if (options?.field) issue.field = options.field;
      if (options?.documentType) issue.documentType = options.documentType;
      return issue as ValidationIssue;
    };

    // 1. Cadastral and Equipment Checks
    if (!equipment) {
      issues.push(createIssue(
        'CADASTRAL',
        'BLOCKER',
        'Equipamento não identificado',
        'O Book de Inspeção não possui vínculo com um equipamento válido no cadastro técnico.',
        'CAD-001',
        { overrideAllowed: false, suggestedAction: 'Vincular o equipamento correto no módulo de Equipamentos.' }
      ));
    } else {
      if (!equipment.tag && !equipment.name) {
        issues.push(createIssue(
          'CADASTRAL',
          'HIGH',
          'TAG do Equipamento não preenchida',
          'O cadastro do equipamento não possui TAG formal registrada.',
          'CAD-002',
          { field: 'tag', suggestedAction: 'Preencher a TAG oficial do equipamento.' }
        ));
      }
    }

    // 2. Inspection Checks
    if (!inspection) {
      issues.push(createIssue(
        'RASTREABILIDADE',
        'BLOCKER',
        'Inspeção não identificada',
        'Dados da inspeção técnica não encontrados ou inacessíveis.',
        'INSP-001',
        { overrideAllowed: false }
      ));
    }

    // 3. Document Matrix Completeness Checks
    for (const item of book.items) {
      // Check 3.1: If NOT_APPLICABLE, verify that justification is present and non-empty
      if (item.applicability === 'NOT_APPLICABLE') {
        if (!item.notApplicableReason || item.notApplicableReason.trim().length < 3) {
          issues.push(createIssue(
            'DOCUMENTAL',
            'CRITICAL',
            `Justificativa ausente para item N/A: ${item.title}`,
            `O documento "${item.title}" foi marcado como NÃO APLICÁVEL, mas nenhuma justificativa técnica válida foi registrada.`,
            'DOC-NA-001',
            {
              documentType: item.documentType,
              section: 'Matriz Documental',
              field: 'notApplicableReason',
              suggestedAction: 'Registrar justificativa técnica de não aplicabilidade.',
              overrideAllowed: true
            }
          ));
        }
      }

      // Check 3.2: If APPLICABLE and REQUIRED, verify availability
      if (item.applicability === 'APPLICABLE') {
        // System generated docs checks
        if (item.documentType === 'INTEGRITY_REPORT' && !report) {
          issues.push(createIssue(
            'DOCUMENTAL',
            'BLOCKER',
            'Relatório de Integridade (RI) ausente',
            'O Relatório de Integridade NR-13 é obrigatório e ainda não foi elaborado para esta inspeção.',
            'DOC-RI-001',
            {
              documentType: 'INTEGRITY_REPORT',
              overrideAllowed: false,
              suggestedAction: 'Elaborar e emitir o Laudo RI no módulo de Relatórios.'
            }
          ));
        }

        if (item.documentType === 'CHECKLIST' && !checklist) {
          issues.push(createIssue(
            'CHECKLIST',
            'BLOCKER',
            'Checklist de Inspeção Visual não preenchido',
            'O Checklist Técnico Visual NR-13 é obrigatório e não foi concluído.',
            'DOC-CHK-001',
            {
              documentType: 'CHECKLIST',
              overrideAllowed: false,
              suggestedAction: 'Executar o Checklist Visual de Inspeção.'
            }
          ));
        }

        // Uploaded document checks (e.g. Line list, Isometrics, Drawings, PIT)
        if (['DRAWINGS', 'LINE_LIST', 'ISOMETRICS', 'PIT', 'FLOW_DIAGRAM'].includes(item.documentType)) {
          if (item.status === 'PENDING' || (!item.attachmentId && !item.fileName && !item.fileUrl)) {
            issues.push(createIssue(
              'DOCUMENTAL',
              'HIGH',
              `Documento aplicável não anexado: ${item.title}`,
              `O documento "${item.title}" está marcado como APLICÁVEL mas nenhum arquivo correspondente foi anexado ao Book.`,
              'DOC-ATT-001',
              {
                documentType: item.documentType,
                suggestedAction: 'Anexar o arquivo técnico ou marcar como NÃO APLICÁVEL com justificativa.'
              }
            ));
          }
        }
      }
    }

    // 4. Integrity Report (RI) Deterministic Checks
    if (report) {
      if (!report.conclusionText || report.conclusionText.trim().length < 10) {
        issues.push(createIssue(
          'TÉCNICA',
          'BLOCKER',
          'Conclusão do Laudo RI incompleta',
          'O Relatório de Integridade não contém parecer conclusivo técnico satisfatório.',
          'RI-001',
          {
            section: '06 - Parecer Conclusivo',
            field: 'conclusionText',
            overrideAllowed: false,
            suggestedAction: 'Completar o parecer técnico conclusivo do PLH.'
          }
        ));
      }

      if (!report.executiveSummary || report.executiveSummary.trim().length < 10) {
        issues.push(createIssue(
          'TÉCNICA',
          'MEDIUM',
          'Resumo Executivo do RI não preenchido',
          'O resumo executivo técnico do Laudo RI está vazio.',
          'RI-002',
          { section: '01 - Resumo Executivo', field: 'executiveSummary' }
        ));
      }

      if (!report.plhSignerId && !report.plhSignatureDate) {
        issues.push(createIssue(
          'ASSINATURA',
          'BLOCKER',
          'Assinatura do Profissional Legalmente Habilitado (PLH) ausente',
          'O Laudo de Integridade exige assinatura formal do Engenheiro / PLH responsável.',
          'SIG-001',
          { overrideAllowed: false, suggestedAction: 'Assinar digitalmente o Laudo RI.' }
        ));
      }
    }

    // 5. Recommendations Deterministic Checks
    if (recommendations && recommendations.length > 0) {
      for (const rec of recommendations) {
        if (!rec.description || rec.description.trim().length < 5) {
          issues.push(createIssue(
            'RECOMENDAÇÃO',
            'HIGH',
            `Recomendação sem descrição detalhada: ${rec.code || rec.id}`,
            'O texto descritivo da recomendação técnica está excessivamente curto ou vago.',
            'REC-001',
            {
              section: 'Recomendações',
              field: 'description',
              suggestedAction: 'Detallar a ação corretiva/preventiva recomendada.'
            }
          ));
        }

        if (!rec.discipline) {
          issues.push(createIssue(
            'RECOMENDAÇÃO',
            'MEDIUM',
            `Recomendação sem disciplina: ${rec.code || rec.id}`,
            'A disciplina técnica responsável (Mecânica, Caldeiraria, Instrumentação, etc.) não foi definida.',
            'REC-002',
            { section: 'Recomendações', field: 'discipline' }
          ));
        }

        if (!rec.criticality) {
          issues.push(createIssue(
            'RECOMENDAÇÃO',
            'HIGH',
            `Recomendação sem criticidade: ${rec.code || rec.id}`,
            'A criticidade (ALTA, MÉDIA, BAIXA) da recomendação técnica não foi classificada.',
            'REC-003',
            { section: 'Recomendações', field: 'criticality' }
          ));
        }

        if (!rec.deadline && !rec.executeIn) {
          issues.push(createIssue(
            'RECOMENDAÇÃO',
            'HIGH',
            `Recomendação sem prazo de execução (Execute In): ${rec.code || rec.id}`,
            'O prazo limite de execução da recomendação não foi definido.',
            'REC-004',
            { section: 'Recomendações', field: 'deadline' }
          ));
        }
      }
    }

    // 6. Cross-Document Checks between Checklist and RI
    if (checklist?.items) {
      const rejectedItems = checklist.items.filter((i: any) => i.status === 'REJECTED' || i.status === 'NON_CONFORMING');
      if (rejectedItems.length > 0 && report) {
        const reportContent = (report.findingsText || '') + (report.conclusionText || '') + (report.bodyText || '');
        const mentionsRejection = rejectedItems.some((item: any) => 
          reportContent.toLowerCase().includes((item.componentName || item.name || '').toLowerCase())
        );

        if (!mentionsRejection && reportContent.length > 0) {
          issues.push(createIssue(
            'CONSISTÊNCIA',
            'HIGH',
            'Divergência entre Checklist e Relatório de Integridade',
            `O checklist registrou ${rejectedItems.length} componente(s) REPROVADO(S), mas o corpo do Laudo RI não faz referência explícita às anomalias detectadas.`,
            'CROSS-001',
            {
              evidence: `Componentes rejeitados no checklist: ${rejectedItems.map((r: any) => r.componentName || r.name).join(', ')}`,
              suggestedAction: 'Verificar se as não-conformidades do Checklist foram refletidas no Laudo RI ou Recomendações.'
            }
          ));
        }
      }
    }

    return issues;
  }
}

export const technicalValidationEngine = new TechnicalValidationEngine();
